import { useEffect, useRef, useState } from "react";
import { copyTextToClipboard } from "./clipboard.js";

const COPIED_RESET_MS = 2500;

/**
 * Shared copy-link control (Prompt 138) - both AgentInviteCardsPage drawer
 * views (CreateInviteCardForm/InviteCardInspector) render the exact same
 * "here is the real invite URL, copy it" UI, so this is the one place that
 * logic lives instead of two byte-identical copies.
 *
 * Tries navigator.clipboard.writeText, falls back to a hidden-textarea
 * execCommand("copy") when the Clipboard API is unavailable/blocked (e.g.
 * an insecure HTTP origin) - see ./clipboard.js. Never fails silently: on
 * fallback failure the URL itself is a real, visible, read-only &lt;input&gt;
 * (not just a &lt;span&gt;) that gets focused+selected automatically, plus an
 * explicit "เลือกทั้งหมด" button that re-selects it if the user clicks
 * elsewhere first - so the worst case is still "select and press Ctrl/Cmd+C
 * yourself," never a dead end with no feedback at all.
 *
 * Prompt 138S: a plain "เปิดลิงก์" (open link) action sits beside copy - a
 * real &lt;a href={url} target="_blank"&gt; anchor, not a click handler, so it
 * opens the exact same URL the Agent would have pasted from copy. This is
 * for owner QA to visually confirm what a worker actually sees at this
 * inviteUrl (the real acceptance signal for Prompt 138S was the copied URL
 * destination, not this button) - it never replaces or changes the copy
 * behavior above.
 */
export default function CopyLinkField({ url }) {
  const [copyState, setCopyState] = useState("idle"); // "idle" | "copied" | "failed"
  const inputRef = useRef(null);
  const resetTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  function selectAll() {
    inputRef.current?.focus();
    inputRef.current?.select();
  }

  async function handleCopy() {
    const succeeded = await copyTextToClipboard(url);
    clearTimeout(resetTimerRef.current);
    if (succeeded) {
      setCopyState("copied");
      resetTimerRef.current = setTimeout(() => setCopyState("idle"), COPIED_RESET_MS);
    } else {
      setCopyState("failed");
      selectAll();
    }
  }

  return (
    <div className="wx-ic-copy-field">
      <input
        ref={inputRef}
        className="wx-ic-copy-input"
        type="text"
        readOnly
        value={url}
        aria-label="Invite URL"
        onFocus={(event) => event.target.select()}
      />
      <div className="wx-ic-copy-row">
        <button type="button" className="wx-button wx-button--primary" onClick={handleCopy}>
          {copyState === "copied" ? "คัดลอกแล้ว" : "คัดลอกลิงก์"}
        </button>
        <a className="wx-button wx-button--ghost" href={url} target="_blank" rel="noopener noreferrer">
          เปิดลิงก์
        </a>
        {copyState === "failed" && (
          <button type="button" className="wx-button wx-button--ghost" onClick={selectAll}>
            เลือกทั้งหมด
          </button>
        )}
      </div>
      {copyState === "failed" && (
        <p className="wx-ic-copy-fallback-note" role="alert">
          คัดลอกอัตโนมัติไม่ได้ กรุณาเลือกและคัดลอกลิงก์นี้
        </p>
      )}
    </div>
  );
}
